// src/components/activity.jsx
// EmailCompose and LogActivity modals.

import { useState, useEffect } from "react";
import * as HS from "../hubspot.js";
import * as MSGraph from "../msgraph.js";
import { aiCall } from "../helpers.js";
import { Btn, FI, Modal, Spin } from "./ui.jsx";

// Compute the sender identity for HubSpot's "log to HubSpot only" fallback
// path. NOT used when sending via M365 -- Graph handles the From header
// automatically based on the signed-in user. Only the fallback (when M365
// sign-in fails or user explicitly chooses log-only) needs this.
//
// Rules:
//   - Admin -> hardcoded Kevin Ardoin's ABM address.
//   - Sales rep -> user.email + name split for first/last.
function senderForUser(user) {
  if (!user) {
    return { email: "noreply@abminternational.com", firstName: "ABM", lastName: "Sales" };
  }
  if (user.isAdmin) {
    return {
      email:     "Kevin.ardoin@abminternational.com",
      firstName: "Kevin",
      lastName:  "Ardoin",
    };
  }
  const fullName = (user.name || "").trim();
  let firstName = "", lastName = "";
  if (fullName) {
    const parts = fullName.split(/\s+/);
    firstName = parts[0] || "";
    lastName  = parts.slice(1).join(" ") || "";
  } else {
    const local = (user.email || "").split("@")[0] || "";
    firstName = local.charAt(0).toUpperCase() + local.slice(1);
    lastName  = "";
  }
  return { email: user.email || "", firstName, lastName };
}

function getCurrentUser() {
  try {
    const id    = localStorage.getItem("crm_cur_user_id");
    const users = JSON.parse(localStorage.getItem("crm_users") || "[]");
    return users.find(u => u.id === id) || null;
  } catch {
    return null;
  }
}

export function EmailCompose({defaultTo, defaultName, onClose}) {
  const [to,   setTo]   = useState(defaultTo || "");
  const [sub,  setSub]  = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiL,     setAiL]     = useState(false);
  const [msg,     setMsg]     = useState("");

  // Microsoft sign-in state. We check on mount whether the rep is already
  // signed in to M365 -- if so they can send real emails; if not, they see
  // a "Sign in to Microsoft" button (with a "Log only" fallback alongside).
  const [msAccount,   setMsAccount]   = useState(null);
  const [msSignInWait, setMsSignInWait] = useState(false);

  // Check existing M365 sign-in state on mount. MSAL's localStorage cache
  // remembers across browser sessions, so a rep who signed in last week is
  // still signed in today.
  useEffect(() => {
    let cancelled = false;
    MSGraph.getSignedInAccount().then(acc => {
      if (!cancelled) setMsAccount(acc);
    });
    return () => { cancelled = true; };
  }, []);

  const draft = async () => {
    setAiL(true);
    try {
      const d = await aiCall(
        'Write a short warm professional sales follow-up email from a mattress sales rep to ' +
        (defaultName || "a customer") +
        '. Return JSON only: {"subject":"...","body":"..."}'
      );
      const t = d.content?.find(b => b.type === "text")?.text || "{}";
      const p = JSON.parse(t.replace(/```json|```/g, "").trim());
      setSub(p.subject || "");
      setBody(p.body || "");
    } catch {}
    setAiL(false);
  };

  // Sign in to Microsoft via popup. On success, update local state so the
  // "Send via Outlook" button replaces the sign-in prompt.
  const handleSignIn = async () => {
    setMsg("");
    setMsSignInWait(true);
    try {
      const acc = await MSGraph.signIn();
      setMsAccount(acc);
      setMsg("Signed in as " + (acc?.username || "Microsoft user") + " ✓");
    } catch (e) {
      // User-cancelled popups are common; suppress noisy error
      if (/user_cancel|cancelled|closed/i.test(e.message || "")) {
        setMsg("");
      } else {
        setMsg("Microsoft sign-in failed: " + e.message);
      }
    }
    setMsSignInWait(false);
  };

  // PRIMARY SEND PATH: M365 Graph sendMail. Email is delivered for real,
  // sender's Outlook gets a Sent record, and HubSpot auto-logs via the BCC
  // address in msgraph.js.
  const sendViaOutlook = async () => {
    if (!to || !sub || !body) return;
    setLoading(true);
    setMsg("");
    try {
      await MSGraph.sendMail({ to, subject: sub, body });
      setMsg("Email sent ✓ (auto-logged to HubSpot via BCC)");
      setTimeout(onClose, 1400);
    } catch (e) {
      // Don't silently fall back -- per business decision, the rep should
      // know if the real send failed. Show the error and let them decide
      // whether to use the explicit "Log only" button.
      setMsg("Send failed: " + e.message);
    }
    setLoading(false);
  };

  // FALLBACK PATH: log a record to HubSpot only (no real email goes out).
  // Mirrors the original behavior of this modal. Used when:
  //   - Rep can't / won't sign in to Microsoft
  //   - Rep explicitly clicks "Log to HubSpot Only" knowing they'll send the
  //     actual email separately from Outlook
  const logOnly = async () => {
    if (!to || !sub || !body) return;
    setLoading(true);
    setMsg("");
    try {
      const sender = senderForUser(getCurrentUser());
      await HS.logEmail(to, sub, body, null, sender);
      setMsg("Logged to HubSpot only — no email sent ✓");
      setTimeout(onClose, 1400);
    } catch (e) {
      setMsg("Error: " + e.message);
    }
    setLoading(false);
  };

  // Disable send buttons unless all fields present
  const canSubmit = !!(to && sub && body);

  return (
    <Modal title="✉  Compose Email" onClose={onClose}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <FI label="To" value={to} onChange={setTo} placeholder="email@company.com" />
        <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
          <div style={{flex:1}}>
            <FI label="Subject" value={sub} onChange={setSub} placeholder="Subject line" />
          </div>
          <Btn variant="ghost" onClick={draft} disabled={aiL} style={{whiteSpace:"nowrap"}}>
            {aiL ? <Spin/> : "✦ AI Draft"}
          </Btn>
        </div>
        <FI label="Body" value={body} onChange={setBody} multiline rows={6} placeholder="Write your message..." />

        {/* M365 status banner -- always visible so the rep knows which account
            their email will send from (or that they need to sign in). */}
        {msAccount ? (
          <div style={{padding:"10px 14px",borderRadius:10,background:"#e6f4ed",border:"1px solid #b2dfcb",color:"var(--green)",fontSize:12,display:"flex",alignItems:"center",gap:8}}>
            <span>✓</span>
            <span>Signed in to Microsoft as <strong>{msAccount.username}</strong></span>
          </div>
        ) : (
          <div style={{padding:"10px 14px",borderRadius:10,background:"#fff8e6",border:"1px solid #fde68a",color:"var(--muted)",fontSize:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
            <span>Not signed in to Microsoft — emails will only log to HubSpot, not actually send.</span>
            <button onClick={handleSignIn} disabled={msSignInWait}
              style={{padding:"5px 12px",borderRadius:8,border:"1px solid var(--brown)",background:"#fff",color:"var(--brown)",fontSize:12,fontWeight:600,cursor:msSignInWait?"wait":"pointer",fontFamily:"Barlow,sans-serif",whiteSpace:"nowrap"}}>
              {msSignInWait ? "Signing in..." : "Sign in to Microsoft"}
            </button>
          </div>
        )}

        {msg && (
          <div style={{padding:"10px 14px",borderRadius:10,background: /failed|error/i.test(msg) ? "#fdecea" : "#e6f4ed",color: /failed|error/i.test(msg) ? "var(--red)" : "var(--green)",fontSize:13,fontWeight:500}}>
            {msg}
          </div>
        )}

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",flexWrap:"wrap"}}>
          <Btn variant="ghost" onClick={onClose} disabled={loading}>Cancel</Btn>
          {/* Show "Log only" as a secondary option whenever NOT signed in, OR
              as a quieter alternative when signed in (in case rep wants to
              draft something they'll personally send from Outlook later). */}
          <Btn variant="ghost" onClick={logOnly} disabled={loading || !canSubmit}>
            {loading ? <><Spin/> ...</> : "Log to HubSpot Only"}
          </Btn>
          {msAccount && (
            <Btn onClick={sendViaOutlook} disabled={loading || !canSubmit}>
              {loading ? <><Spin/> Sending...</> : "Send via Outlook"}
            </Btn>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function LogActivity({company, contact, onClose, onLogged, currentUser}) {
  const [type,    setType]    = useState("note");
  const [body,    setBody]    = useState("");
  const [loading, setLoading] = useState(false);

  const log = async () => {
    if (!body) return;
    setLoading(true);
    const today = new Date();
    const dateStr = today.toLocaleDateString();
    const timeStr = today.toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"});
    try {
      if (type === "note") await HS.logNote(body, company?.id, contact?.id);
      else                 await HS.logCall(body, company?.id, contact?.id);

      // Increment local quota counters
      const period = "month";
      const userId = currentUser?.id || window.__crmUserId || localStorage.getItem("crm_cur_user_id") || "admin";
      const key = "crm_actuals_" + userId + "_" + period;
      const stored = localStorage.getItem(key);
      const actuals = stored ? JSON.parse(stored) : {calls:0, visits:0, emails:0, quotes:0, sales:0};
      if (type === "call") actuals.calls = (actuals.calls || 0) + 1;
      localStorage.setItem(key, JSON.stringify(actuals));

      // Notify parent so the activity feed refreshes immediately
      const newAct = {type, summary: body, date: dateStr, time: timeStr, local: true};
      if (onLogged) onLogged(newAct);
    } catch (e) {
      console.error("Log failed:", e.message);
    }
    onClose();
    setLoading(false);
  };

  return (
    <Modal title={"📋 Log " + (type === "note" ? "Note" : "Call")} onClose={onClose}>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div style={{display:"flex",gap:8}}>
          {["note","call"].map(t => (
            <button key={t} onClick={()=>setType(t)}
              style={{
                flex:1,padding:"10px 0",borderRadius:10,
                border:"1.5px solid " + (type === t ? "var(--brown)" : "var(--warm2)"),
                background:type === t ? "var(--warm1)" : "#fff",
                color:type === t ? "var(--brown)" : "var(--muted)",
                fontWeight:600,fontSize:13,cursor:"pointer",fontFamily:"Barlow,sans-serif",
              }}>
              {t === "note" ? "📝 Note" : "📞 Call Log"}
            </button>
          ))}
        </div>
        <FI
          label={type === "note" ? "Note" : "Call summary"}
          value={body} onChange={setBody}
          multiline rows={5}
          placeholder={type === "note" ? "What happened?" : "Who did you speak with?"}
        />
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={log} disabled={loading || !body}>
            {loading ? <><Spin/> Saving...</> : "Save to HubSpot"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
